import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useGoogleMaps } from '../hooks/useGoogleMaps';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import BusinessSelector from '../components/BusinessSelector';
import { 
  Calendar, DollarSign, Package, Music, Upload, Loader2, 
  Plus, Trash2, Edit, Star, Building2, MapPin, Phone, Mail,
  Image as ImageIcon, CheckCircle, AlertCircle, Eye, Settings, Send, Instagram
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { formatDateUK, formatCurrency } from '../utils/formatters';
import AddressAutocomplete from '../components/AddressAutocomplete';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const DAYS_OF_WEEK = [
  { value: '0', label: 'Monday' },
  { value: '1', label: 'Tuesday' },
  { value: '2', label: 'Wednesday' },
  { value: '3', label: 'Thursday' },
  { value: '4', label: 'Friday' },
  { value: '5', label: 'Saturday' },
  { value: '6', label: 'Sunday' }
];

const HOURS = Array.from({ length: 24 }, (_, i) => ({
  value: String(i),
  label: `${i.toString().padStart(2, '0')}:00`
}));

const BusinessDashboard = () => {
  const navigate = useNavigate();
  const { user, business: authBusiness, businesses, activeBusiness } = useAuth();
  
  // Load Google Maps for address autocomplete
  useGoogleMaps();
  
  const [loading, setLoading] = useState(true);
  const [business, setBusiness] = useState(null);
  const [systems, setSystems] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [equipmentBookings, setEquipmentBookings] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Digest settings state
  const [digestSettings, setDigestSettings] = useState({
    enabled: true,
    day_of_week: 0,
    hour: 9,
    timezone: 'Africa/Johannesburg'
  });
  const [savingDigest, setSavingDigest] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);
  
  // Instagram handle state
  const [instagramHandle, setInstagramHandle] = useState('');
  const [savingInstagram, setSavingInstagram] = useState(false);
  
  // Address state
  const [businessAddress, setBusinessAddress] = useState('');
  const [savingAddress, setSavingAddress] = useState(false);
  
  // PA System form state
  const [showSystemModal, setShowSystemModal] = useState(false);
  const [editingSystem, setEditingSystem] = useState(null);
  const [systemForm, setSystemForm] = useState({
    name: '', category: '', description: '', price: '',
    price_unit: 'per day', capacity: '', features: '',
    spec_power: '', spec_coverage: '', spec_setup: '', spec_microphones: ''
  });
  const [systemImage, setSystemImage] = useState(null);
  const [systemImagePreview, setSystemImagePreview] = useState(null);
  const systemFileInputRef = useRef(null);
  
  // Equipment form state
  const [showEquipmentModal, setShowEquipmentModal] = useState(false);
  const [equipmentForm, setEquipmentForm] = useState({
    name: '', description: '', category: '', price: '',
    price_unit: 'per day', quantity_available: 1
  });
  const [equipmentImage, setEquipmentImage] = useState(null);
  const [equipmentImagePreview, setEquipmentImagePreview] = useState(null);
  const equipmentFileInputRef = useRef(null);
  
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  
  // Track if initial data has been fetched to prevent infinite loops
  const hasFetchedRef = useRef(false);

  const fetchData = useCallback(async (businessId) => {
    try {
      setLoading(true);
      
      const [businessRes, systemsRes, equipmentRes, bookingsRes, eqBookingsRes, digestRes] = await Promise.all([
        axios.get(`${API}/businesses/${businessId}`, { withCredentials: true }),
        axios.get(`${API}/systems?business_id=${businessId}`, { withCredentials: true }),
        axios.get(`${API}/equipment?business_id=${businessId}`, { withCredentials: true }),
        axios.get(`${API}/business/bookings`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/business/equipment-bookings`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/digest/settings`, { withCredentials: true }).catch(() => ({ data: { enabled: true, day_of_week: 0, hour: 9 } }))
      ]);
      
      setBusiness(businessRes.data);
      setSystems(Array.isArray(systemsRes.data) ? systemsRes.data : []);
      setEquipment(Array.isArray(equipmentRes.data) ? equipmentRes.data : []);
      setBookings(Array.isArray(bookingsRes.data) ? bookingsRes.data : []);
      setEquipmentBookings(Array.isArray(eqBookingsRes.data) ? eqBookingsRes.data : []);
      // Set Instagram handle from business data
      setInstagramHandle(businessRes.data?.instagram_handle || '');
      // Set address from business data
      const addr = businessRes.data?.address;
      if (addr) {
        setBusinessAddress(`${addr.street || ''}, ${addr.city || ''}, ${addr.province || ''}`.replace(/^, |, $/g, ''));
      }
      if (digestRes.data) {
        setDigestSettings({
          enabled: digestRes.data.enabled ?? true,
          day_of_week: digestRes.data.day_of_week ?? 0,
          hour: digestRes.data.hour ?? 9,
          timezone: digestRes.data.timezone ?? 'Africa/Johannesburg'
        });
      }
    } catch (error) {
      console.error('Failed to fetch business data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Prefer activeBusiness from multi-business context, fallback to authBusiness
    const businessId = activeBusiness?.business_id || authBusiness?.business_id;
    if (businessId && !hasFetchedRef.current) {
      hasFetchedRef.current = true;
      fetchData(businessId);
    } else if (!businessId) {
      setLoading(false);
    }
  }, [activeBusiness?.business_id, authBusiness?.business_id, fetchData]);

  // Reset fetch ref when business changes (for business switching)
  useEffect(() => {
    const newBusinessId = activeBusiness?.business_id || authBusiness?.business_id;
    if (business && business.business_id !== newBusinessId) {
      hasFetchedRef.current = false;
    }
  }, [activeBusiness?.business_id, authBusiness?.business_id, business]);

  const handleSaveDigestSettings = async () => {
    setSavingDigest(true);
    try {
      await axios.put(`${API}/digest/settings`, digestSettings, { withCredentials: true });
      alert('Email digest settings saved!');
    } catch (error) {
      alert('Failed to save settings');
    } finally {
      setSavingDigest(false);
    }
  };

  const handleSendDigestNow = async () => {
    setSendingDigest(true);
    try {
      const res = await axios.post(`${API}/digest/send-now`, {}, { withCredentials: true });
      alert(`Digest email sent! Check ${business?.contact_email || user?.email}`);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to send digest email');
    } finally {
      setSendingDigest(false);
    }
  };

  const handleSaveInstagramHandle = async () => {
    setSavingInstagram(true);
    try {
      const formData = new FormData();
      formData.append('instagram_handle', instagramHandle);
      await axios.put(`${API}/business/profile`, formData, { withCredentials: true });
      setBusiness(prev => ({ ...prev, instagram_handle: instagramHandle.replace('@', '') }));
      alert('Instagram handle saved!');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to save Instagram handle');
    } finally {
      setSavingInstagram(false);
    }
  };

  const handleSaveAddress = async () => {
    if (!businessAddress.trim()) {
      alert('Please enter an address');
      return;
    }
    setSavingAddress(true);
    try {
      const formData = new FormData();
      formData.append('address', businessAddress);
      await axios.put(`${API}/business/profile`, formData, { withCredentials: true });
      alert('Business address updated!');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to save address');
    } finally {
      setSavingAddress(false);
    }
  };

  // PA System handlers
  const handleSystemImageChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      setSystemImage(file);
      setSystemImagePreview(URL.createObjectURL(file));
      setFormError('');
    }
  };

  const openAddSystemModal = () => {
    setEditingSystem(null);
    setSystemForm({
      name: '', category: '', description: '', price: '',
      price_unit: 'per day', capacity: '', features: '',
      spec_power: '', spec_coverage: '', spec_setup: '', spec_microphones: ''
    });
    setSystemImage(null);
    setSystemImagePreview(null);
    setFormError('');
    setShowSystemModal(true);
  };

  const openEditSystemModal = (system) => {
    setEditingSystem(system);
    setSystemForm({
      name: system.name,
      category: system.category,
      description: system.description,
      price: system?.price?.toString() || '',
      price_unit: system?.price_unit || 'per day',
      capacity: system?.capacity || '',
      features: system?.features?.join(', ') || '',
      spec_power: system?.specs?.power || '',
      spec_coverage: system?.specs?.coverage || '',
      spec_setup: system?.specs?.setup || '',
      spec_microphones: system?.specs?.microphones || ''
    });
    setSystemImage(null);
    const imgUrl = system?.image || '';
    setSystemImagePreview(imgUrl.startsWith('/api') ? `${BACKEND_URL}${imgUrl}` : imgUrl);
    setFormError('');
    setShowSystemModal(true);
  };

  const handleSubmitSystem = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!editingSystem && !systemImage) {
      setFormError('Please select an image');
      return;
    }
    setSubmitting(true);
    try {
      const formData = new FormData();
      Object.entries(systemForm).forEach(([key, val]) => formData.append(key, val));
      if (systemImage) formData.append('image', systemImage);

      if (editingSystem) {
        await axios.put(`${API}/business/systems/${editingSystem.system_id}`, formData, {
          withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        await axios.post(`${API}/business/systems`, formData, {
          withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      setShowSystemModal(false);
      fetchData(authBusiness?.business_id);
    } catch (error) {
      setFormError(error.response?.data?.detail || 'Failed to save PA system');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteSystem = async (systemId) => {
    if (!window.confirm('Delete this PA system?')) return;
    try {
      await axios.delete(`${API}/business/systems/${systemId}`, { withCredentials: true });
      fetchData(authBusiness?.business_id);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to delete');
    }
  };

  // Equipment handlers
  const handleEquipmentImageChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      setEquipmentImage(file);
      setEquipmentImagePreview(URL.createObjectURL(file));
      setFormError('');
    }
  };

  const openAddEquipmentModal = () => {
    setEquipmentForm({
      name: '', description: '', category: '', price: '',
      price_unit: 'per day', quantity_available: 1
    });
    setEquipmentImage(null);
    setEquipmentImagePreview(null);
    setFormError('');
    setShowEquipmentModal(true);
  };

  const handleSubmitEquipment = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!equipmentImage) {
      setFormError('Please select an image');
      return;
    }
    setSubmitting(true);
    try {
      const formData = new FormData();
      Object.entries(equipmentForm).forEach(([key, val]) => formData.append(key, val));
      formData.append('image', equipmentImage);

      await axios.post(`${API}/business/equipment`, formData, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }
      });
      setShowEquipmentModal(false);
      fetchData(authBusiness?.business_id);
    } catch (error) {
      setFormError(error.response?.data?.detail || 'Failed to add equipment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteEquipment = async (equipmentId) => {
    if (!window.confirm('Delete this equipment?')) return;
    try {
      await axios.delete(`${API}/business/equipment/${equipmentId}`, { withCredentials: true });
      fetchData(authBusiness?.business_id);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      confirmed: 'bg-green-500', pending: 'bg-yellow-500',
      cancelled: 'bg-red-500', completed: 'bg-blue-500'
    };
    return colors[status] || 'bg-gray-500';
  };

  const renderStars = (rating) => {
    if (!rating) return <span className="text-gray-500 text-sm">Not rated</span>;
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star key={star} className={`h-4 w-4 ${star <= rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-600'}`} />
        ))}
        <span className="text-sm ml-1 text-gray-400">({rating})</span>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <Loader2 className="h-12 w-12 animate-spin text-yellow-500" />
      </div>
    );
  }

  if (!authBusiness?.business_id && (!businesses || businesses.length === 0)) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <Card className="bg-gray-800 border-gray-700 max-w-md w-full mx-4">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-2" data-testid="no-business-profile-title">No Business Profile</h2>
            <p className="text-gray-400 mb-6">
              You have not set up your business profile yet. Complete your business registration to access the dashboard.
            </p>
            <div className="space-y-3">
              <Button 
                className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-semibold"
                onClick={() => navigate('/become-vendor')}
                data-testid="register-new-business-btn"
              >
                <Building2 className="h-4 w-4 mr-2" />
                Register New Business
              </Button>
              <Button 
                variant="outline"
                className="w-full border-gray-600 text-gray-300 hover:bg-gray-700"
                onClick={() => navigate('/become-vendor?resume=true')}
                data-testid="resume-draft-registration-btn"
              >
                <Edit className="h-4 w-4 mr-2" />
                Resume Draft Registration
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const stats = {
    totalBookings: bookings.length + equipmentBookings.length,
    pendingBookings: [...bookings, ...equipmentBookings].filter(b => b.status === 'pending').length,
    totalRevenue: [...bookings, ...equipmentBookings].reduce((sum, b) => sum + (b.total_amount || 0), 0),
    avgRating: business?.average_rating || 0
  };

  return (
    <div className="min-h-screen bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Business Selector - Multi-business support */}
        {businesses && businesses.length > 0 && (
          <BusinessSelector className="mb-6" />
        )}

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-gray-700 border-2 border-yellow-500 flex items-center justify-center overflow-hidden">
              {business?.logo_url ? (
                <img src={business.logo_url.startsWith('/') ? `${BACKEND_URL}${business.logo_url}` : business.logo_url} 
                     alt="Logo" className="w-full h-full object-cover" />
              ) : (
                <Building2 className="h-8 w-8 text-yellow-500" />
              )}
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">{business?.business_name}</h1>
              <div className="flex items-center gap-2 mt-1">
                <Badge className={business?.verification_status === 'verified' ? 'bg-green-500' : 'bg-yellow-500'}>
                  {business?.verification_status === 'verified' ? (
                    <><CheckCircle className="h-3 w-3 mr-1" /> Verified</>
                  ) : (
                    <><AlertCircle className="h-3 w-3 mr-1" /> {business?.verification_status}</>
                  )}
                </Badge>
                {renderStars(business?.average_rating)}
              </div>
            </div>
          </div>
          <Button variant="outline" className="border-gray-600 text-gray-300 hover:bg-gray-800"
                  onClick={() => navigate(`/business/${business?.business_id}`)}>
            <Eye className="h-4 w-4 mr-2" /> View Public Profile
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Total Bookings</p>
                  <p className="text-2xl font-bold text-white">{stats.totalBookings}</p>
                </div>
                <Calendar className="h-8 w-8 text-yellow-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Pending</p>
                  <p className="text-2xl font-bold text-yellow-500">{stats.pendingBookings}</p>
                </div>
                <AlertCircle className="h-8 w-8 text-yellow-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Revenue</p>
                  <p className="text-2xl font-bold text-green-500">{formatCurrency(stats.totalRevenue)}</p>
                </div>
                <DollarSign className="h-8 w-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Rating</p>
                  <p className="text-2xl font-bold text-yellow-500">{stats.avgRating || '-'}</p>
                </div>
                <Star className="h-8 w-8 text-yellow-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full max-w-3xl grid-cols-5 mb-6 bg-gray-800">
            <TabsTrigger value="overview" className="data-[state=active]:bg-yellow-500 data-[state=active]:text-black">
              Overview
            </TabsTrigger>
            <TabsTrigger value="systems" className="data-[state=active]:bg-yellow-500 data-[state=active]:text-black">
              PA Systems ({systems.length})
            </TabsTrigger>
            <TabsTrigger value="equipment" className="data-[state=active]:bg-yellow-500 data-[state=active]:text-black">
              Equipment ({equipment.length})
            </TabsTrigger>
            <TabsTrigger value="bookings" className="data-[state=active]:bg-yellow-500 data-[state=active]:text-black">
              Bookings ({stats.totalBookings})
            </TabsTrigger>
            <TabsTrigger value="settings" className="data-[state=active]:bg-yellow-500 data-[state=active]:text-black">
              <Settings className="h-4 w-4 mr-1" /> Settings
            </TabsTrigger>
          </TabsList>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Mail className="h-5 w-5 text-yellow-500" />
                    Weekly Stats Email Digest
                  </CardTitle>
                  <CardDescription className="text-gray-400">
                    Receive a weekly summary of your business performance via email
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-white">Enable Email Digest</Label>
                      <p className="text-sm text-gray-500">Get weekly stats delivered to your inbox</p>
                    </div>
                    <Switch
                      checked={digestSettings.enabled}
                      onCheckedChange={(checked) => setDigestSettings({...digestSettings, enabled: checked})}
                    />
                  </div>
                  
                  {digestSettings.enabled && (
                    <>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-gray-300">Day of Week</Label>
                          <Select 
                            value={String(digestSettings.day_of_week)} 
                            onValueChange={(v) => setDigestSettings({...digestSettings, day_of_week: parseInt(v)})}
                          >
                            <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-gray-800 border-gray-700">
                              {DAYS_OF_WEEK.map(day => (
                                <SelectItem key={day.value} value={day.value} className="text-gray-300">{day.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-gray-300">Time</Label>
                          <Select 
                            value={String(digestSettings.hour)} 
                            onValueChange={(v) => setDigestSettings({...digestSettings, hour: parseInt(v)})}
                          >
                            <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-gray-800 border-gray-700 max-h-48">
                              {HOURS.map(hour => (
                                <SelectItem key={hour.value} value={hour.value} className="text-gray-300">{hour.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      
                      <div className="p-3 bg-gray-700 rounded-lg">
                        <p className="text-sm text-gray-300">
                          Digest will be sent every <strong className="text-yellow-500">{DAYS_OF_WEEK.find(d => d.value === String(digestSettings.day_of_week))?.label}</strong> at{' '}
                          <strong className="text-yellow-500">{String(digestSettings.hour).padStart(2, '0')}:00</strong> (South Africa time)
                        </p>
                      </div>
                    </>
                  )}
                  
                  <div className="flex gap-3">
                    <Button 
                      onClick={handleSaveDigestSettings}
                      disabled={savingDigest}
                      className="bg-yellow-500 hover:bg-yellow-600 text-black"
                    >
                      {savingDigest ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                      Save Settings
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={handleSendDigestNow}
                      disabled={sendingDigest}
                      className="border-gray-600 text-gray-300 hover:bg-gray-700"
                    >
                      {sendingDigest ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                      Send Now
                    </Button>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white">What is in the digest?</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-yellow-500/20 rounded-lg">
                        <Calendar className="h-4 w-4 text-yellow-500" />
                      </div>
                      <div>
                        <p className="font-medium text-white">Booking Summary</p>
                        <p className="text-sm text-gray-400">Total, new, and confirmed bookings for the week</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-green-500/20 rounded-lg">
                        <DollarSign className="h-4 w-4 text-green-500" />
                      </div>
                      <div>
                        <p className="font-medium text-white">Revenue Report</p>
                        <p className="text-sm text-gray-400">Your earnings from confirmed bookings</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-yellow-500/20 rounded-lg">
                        <Star className="h-4 w-4 text-yellow-500" />
                      </div>
                      <div>
                        <p className="font-medium text-white">Rating Updates</p>
                        <p className="text-sm text-gray-400">Current rating and total reviews</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-blue-500/20 rounded-lg">
                        <Calendar className="h-4 w-4 text-blue-500" />
                      </div>
                      <div>
                        <p className="font-medium text-white">Upcoming Events</p>
                        <p className="text-sm text-gray-400">Events scheduled for the next 7 days</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
            
            {/* Instagram Profile Section */}
            <Card className="bg-gray-800 border-gray-700 mt-6">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Instagram className="h-5 w-5 text-pink-500" />
                  Instagram Profile
                </CardTitle>
                <CardDescription className="text-gray-400">
                  Connect your Instagram to showcase your work on your public profile
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-gray-300">Instagram Username</Label>
                  <div className="flex gap-3">
                    <div className="relative flex-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">@</span>
                      <Input 
                        className="bg-gray-700 border-gray-600 text-white pl-8"
                        placeholder="your_instagram_handle"
                        value={instagramHandle}
                        onChange={(e) => setInstagramHandle(e.target.value.replace('@', ''))}
                      />
                    </div>
                    <Button 
                      onClick={handleSaveInstagramHandle}
                      disabled={savingInstagram}
                      className="bg-gradient-to-r from-purple-500 via-pink-500 to-yellow-500 text-white hover:opacity-90"
                    >
                      {savingInstagram ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                      Save
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500">
                    Enter your Instagram username without the @ symbol. Your profile will be embedded on your public business page.
                  </p>
                </div>
                
                {business?.instagram_handle && (
                  <div className="p-4 bg-gray-700 rounded-lg flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 via-pink-500 to-yellow-500 p-[2px]">
                        <div className="w-full h-full rounded-full bg-gray-800 flex items-center justify-center">
                          <Instagram className="h-5 w-5 text-white" />
                        </div>
                      </div>
                      <div>
                        <p className="text-white font-medium">@{business.instagram_handle}</p>
                        <p className="text-xs text-gray-400">Currently connected</p>
                      </div>
                    </div>
                    <a 
                      href={`https://instagram.com/${business.instagram_handle}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-pink-400 hover:text-pink-300 text-sm flex items-center gap-1"
                    >
                      View Profile <Eye className="h-3 w-3" />
                    </a>
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Business Address Section */}
            <Card className="bg-gray-800 border-gray-700 mt-6">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-yellow-500" />
                  Business Address
                </CardTitle>
                <CardDescription className="text-gray-400">
                  Update your business location for customers to find you easily
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <AddressAutocomplete
                    value={businessAddress}
                    onChange={setBusinessAddress}
                    label="Business Address"
                    placeholder="Start typing your business address..."
                  />
                </div>
                
                <Button 
                  onClick={handleSaveAddress}
                  disabled={savingAddress}
                  className="bg-yellow-500 hover:bg-yellow-600 text-black"
                  data-testid="save-address-btn"
                >
                  {savingAddress ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                  Save Address
                </Button>
                
                {business?.address && (
                  <div className="p-4 bg-gray-700 rounded-lg">
                    <p className="text-sm text-gray-400 mb-1">Current Address:</p>
                    <div className="flex items-center gap-2 text-white">
                      <MapPin className="h-4 w-4 text-yellow-500" />
                      <span>{business.address.street}, {business.address.city}, {business.address.province}</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white">Business Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {business?.description && (
                    <p className="text-gray-400">{business.description}</p>
                  )}
                  <div className="space-y-2">
                    {business?.address && (
                      <div className="flex items-center gap-2 text-gray-300">
                        <MapPin className="h-4 w-4 text-yellow-500" />
                        <span>{business.address.street}, {business.address.city}, {business.address.province}</span>
                      </div>
                    )}
                    {business?.contact_phone && (
                      <div className="flex items-center gap-2 text-gray-300">
                        <Phone className="h-4 w-4 text-yellow-500" />
                        <span>{business.contact_phone}</span>
                      </div>
                    )}
                    {business?.contact_email && (
                      <div className="flex items-center gap-2 text-gray-300">
                        <Mail className="h-4 w-4 text-yellow-500" />
                        <span>{business.contact_email}</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white">Recent Bookings</CardTitle>
                </CardHeader>
                <CardContent>
                  {[...bookings, ...equipmentBookings].slice(0, 5).length > 0 ? (
                    <div className="space-y-3">
                      {[...bookings, ...equipmentBookings].slice(0, 5).map((booking) => (
                        <div key={booking.booking_id} className="flex justify-between items-center p-3 bg-gray-700 rounded-lg">
                          <div>
                            <p className="text-white font-medium">{booking.event_name}</p>
                            <p className="text-sm text-gray-400">{formatDateUK(booking.event_date)}</p>
                          </div>
                          <Badge className={`${getStatusColor(booking.status)} text-white`}>
                            {booking.status}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-4">No bookings yet</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* PA Systems Tab */}
          <TabsContent value="systems">
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="text-white">PA System Packages</CardTitle>
                    <CardDescription className="text-gray-400">Manage your PA system offerings</CardDescription>
                  </div>
                  <Button onClick={openAddSystemModal} className="bg-yellow-500 hover:bg-yellow-600 text-black">
                    <Plus className="h-4 w-4 mr-2" /> Add Package
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {systems.length > 0 ? (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {systems.map((system) => (
                      <Card key={system.system_id} className="bg-gray-700 border-gray-600">
                        <div className="aspect-video relative overflow-hidden rounded-t-lg">
                          <img src={system.image?.startsWith('/api') ? `${BACKEND_URL}${system.image}` : system.image}
                               alt={system.name} className="w-full h-full object-cover" />
                          <Badge className="absolute top-2 right-2 bg-yellow-500 text-black">{system.category}</Badge>
                        </div>
                        <CardContent className="pt-4">
                          <h3 className="font-semibold text-white mb-1">{system.name}</h3>
                          <p className="text-sm text-gray-400 line-clamp-2 mb-2">{system.description}</p>
                          <div className="flex justify-between items-center mb-3">
                            <span className="text-lg font-bold text-yellow-500">{formatCurrency(system.price)}</span>
                            <span className="text-sm text-gray-500">{system.price_unit}</span>
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={() => openEditSystemModal(system)}
                                    className="border-gray-600 text-gray-300 hover:bg-gray-600">
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => handleDeleteSystem(system.system_id)}
                                    className="border-red-600 text-red-500 hover:bg-red-600 hover:text-white">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Music className="h-16 w-16 text-gray-600 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-white mb-2">No PA Systems</h3>
                    <p className="text-gray-400 mb-4">Add your PA system packages for customers to book.</p>
                    <Button onClick={openAddSystemModal} className="bg-yellow-500 hover:bg-yellow-600 text-black">
                      <Plus className="h-4 w-4 mr-2" /> Add First Package
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Equipment Tab */}
          <TabsContent value="equipment">
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="text-white">Equipment Inventory</CardTitle>
                    <CardDescription className="text-gray-400">Individual equipment items for rent</CardDescription>
                  </div>
                  <Button onClick={openAddEquipmentModal} className="bg-yellow-500 hover:bg-yellow-600 text-black">
                    <Plus className="h-4 w-4 mr-2" /> Add Equipment
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {equipment.length > 0 ? (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {equipment.map((item) => (
                      <Card key={item.equipment_id} className="bg-gray-700 border-gray-600">
                        <div className="aspect-video relative overflow-hidden rounded-t-lg">
                          <img src={item.image?.startsWith('/api') ? `${BACKEND_URL}${item.image}` : item.image}
                               alt={item.name} className="w-full h-full object-cover" />
                          <Badge className="absolute top-2 right-2 bg-yellow-500 text-black">{item.category}</Badge>
                        </div>
                        <CardContent className="pt-4">
                          <h3 className="font-semibold text-white mb-1">{item.name}</h3>
                          <p className="text-sm text-gray-400 line-clamp-2 mb-2">{item.description}</p>
                          <div className="flex justify-between items-center mb-3">
                            <span className="text-lg font-bold text-yellow-500">{formatCurrency(item.price)}</span>
                            <span className="text-sm text-gray-500">Qty: {item.quantity_available}</span>
                          </div>
                          <Button size="sm" variant="outline" onClick={() => handleDeleteEquipment(item.equipment_id)}
                                  className="w-full border-red-600 text-red-500 hover:bg-red-600 hover:text-white">
                            <Trash2 className="h-4 w-4 mr-2" /> Delete
                          </Button>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Package className="h-16 w-16 text-gray-600 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-white mb-2">No Equipment</h3>
                    <p className="text-gray-400 mb-4">Add equipment items for customers to rent.</p>
                    <Button onClick={openAddEquipmentModal} className="bg-yellow-500 hover:bg-yellow-600 text-black">
                      <Plus className="h-4 w-4 mr-2" /> Add Equipment
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Bookings Tab */}
          <TabsContent value="bookings">
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white">All Bookings</CardTitle>
                <CardDescription className="text-gray-400">Manage bookings for your business</CardDescription>
              </CardHeader>
              <CardContent>
                {[...bookings, ...equipmentBookings].length > 0 ? (
                  <div className="space-y-4">
                    {[...bookings, ...equipmentBookings].map((booking) => (
                      <Card key={booking.booking_id} className="bg-gray-700 border-gray-600">
                        <CardContent className="pt-4">
                          <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
                            <div>
                              <h3 className="text-lg font-semibold text-white">{booking.event_name}</h3>
                              <p className="text-sm text-gray-400">{booking.user_name} • {booking.user_email}</p>
                              <div className="flex gap-4 mt-2 text-sm text-gray-400">
                                <span><Calendar className="inline h-4 w-4 mr-1" />{formatDateUK(booking.event_date)}</span>
                                <span><MapPin className="inline h-4 w-4 mr-1" />{booking.location}</span>
                              </div>
                            </div>
                            <div className="flex flex-col items-end gap-2">
                              <div className="flex gap-2">
                                <Badge className={`${getStatusColor(booking.status)} text-white`}>{booking.status}</Badge>
                                <Badge className={booking.payment_status === 'paid' ? 'bg-green-500' : 'bg-yellow-500'}>
                                  {booking.payment_status}
                                </Badge>
                              </div>
                              <span className="text-lg font-bold text-yellow-500">{formatCurrency(booking.total_amount)}</span>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Calendar className="h-16 w-16 text-gray-600 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-white mb-2">No Bookings Yet</h3>
                    <p className="text-gray-400">When customers book your services, they will appear here.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* PA System Modal */}
      <Dialog open={showSystemModal} onOpenChange={setShowSystemModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-gray-800 border-gray-700 text-white">
          <DialogHeader>
            <DialogTitle>{editingSystem ? 'Edit PA Package' : 'Add New PA Package'}</DialogTitle>
            <DialogDescription className="text-gray-400">
              {editingSystem ? 'Update the PA system details' : 'Create a new PA system package'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitSystem} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Package Name *</Label>
                <Input className="bg-gray-700 border-gray-600 text-white" placeholder="e.g., Premium PA System"
                       value={systemForm.name} onChange={(e) => setSystemForm({...systemForm, name: e.target.value})} required />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Category *</Label>
                <Select value={systemForm.category} onValueChange={(v) => setSystemForm({...systemForm, category: v})}>
                  <SelectTrigger className="bg-gray-700 border-gray-600 text-white"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700">
                    {['Basic', 'Premium', 'Professional', 'Conference', 'Festival'].map(cat => (
                      <SelectItem key={cat} value={cat} className="text-gray-300">{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Description *</Label>
              <Textarea className="bg-gray-700 border-gray-600 text-white" rows={2}
                        value={systemForm.description} onChange={(e) => setSystemForm({...systemForm, description: e.target.value})} required />
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Price (ZAR) *</Label>
                <Input type="number" min="0" className="bg-gray-700 border-gray-600 text-white"
                       value={systemForm.price} onChange={(e) => setSystemForm({...systemForm, price: e.target.value})} required />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Price Unit</Label>
                <Select value={systemForm.price_unit} onValueChange={(v) => setSystemForm({...systemForm, price_unit: v})}>
                  <SelectTrigger className="bg-gray-700 border-gray-600 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700">
                    {['per day', 'per hour', 'per event'].map(unit => (
                      <SelectItem key={unit} value={unit} className="text-gray-300">{unit}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Capacity *</Label>
                <Input className="bg-gray-700 border-gray-600 text-white" placeholder="Up to 200 people"
                       value={systemForm.capacity} onChange={(e) => setSystemForm({...systemForm, capacity: e.target.value})} required />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Features (comma-separated) *</Label>
              <Textarea className="bg-gray-700 border-gray-600 text-white" rows={2} placeholder="4 Speakers, 2 Microphones, Mixer"
                        value={systemForm.features} onChange={(e) => setSystemForm({...systemForm, features: e.target.value})} required />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <Input className="bg-gray-700 border-gray-600 text-white" placeholder="Power (e.g., 4000W)"
                     value={systemForm.spec_power} onChange={(e) => setSystemForm({...systemForm, spec_power: e.target.value})} />
              <Input className="bg-gray-700 border-gray-600 text-white" placeholder="Coverage (e.g., 200 people)"
                     value={systemForm.spec_coverage} onChange={(e) => setSystemForm({...systemForm, spec_coverage: e.target.value})} />
              <Input className="bg-gray-700 border-gray-600 text-white" placeholder="Setup Time (e.g., 30 min)"
                     value={systemForm.spec_setup} onChange={(e) => setSystemForm({...systemForm, spec_setup: e.target.value})} />
              <Input className="bg-gray-700 border-gray-600 text-white" placeholder="Microphones (e.g., 4 wireless)"
                     value={systemForm.spec_microphones} onChange={(e) => setSystemForm({...systemForm, spec_microphones: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Image {!editingSystem && '*'}</Label>
              <div className="border-2 border-dashed border-gray-600 rounded-lg p-4 text-center cursor-pointer hover:border-yellow-500"
                   onClick={() => systemFileInputRef.current?.click()}>
                {systemImagePreview ? (
                  <img src={systemImagePreview} alt="Preview" className="max-h-32 mx-auto rounded" />
                ) : (
                  <div><Upload className="h-8 w-8 text-gray-500 mx-auto mb-2" /><p className="text-sm text-gray-500">Click to upload</p></div>
                )}
                <input ref={systemFileInputRef} type="file" accept="image/*" className="hidden" onChange={handleSystemImageChange} />
              </div>
            </div>
            {formError && <div className="p-3 bg-red-900/50 text-red-400 rounded text-sm">{formError}</div>}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowSystemModal(false)}
                      className="border-gray-600 text-gray-300">Cancel</Button>
              <Button type="submit" disabled={submitting} className="bg-yellow-500 hover:bg-yellow-600 text-black">
                {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                {editingSystem ? 'Update' : 'Add'} Package
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Equipment Modal */}
      <Dialog open={showEquipmentModal} onOpenChange={setShowEquipmentModal}>
        <DialogContent className="max-w-2xl bg-gray-800 border-gray-700 text-white">
          <DialogHeader>
            <DialogTitle>Add New Equipment</DialogTitle>
            <DialogDescription className="text-gray-400">Add equipment to your inventory</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitEquipment} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Name *</Label>
                <Input className="bg-gray-700 border-gray-600 text-white" placeholder="JBL Speaker"
                       value={equipmentForm.name} onChange={(e) => setEquipmentForm({...equipmentForm, name: e.target.value})} required />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Category *</Label>
                <Select value={equipmentForm.category} onValueChange={(v) => setEquipmentForm({...equipmentForm, category: v})}>
                  <SelectTrigger className="bg-gray-700 border-gray-600 text-white"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700">
                    {['Speaker', 'Microphone', 'Mixer', 'Amplifier', 'Subwoofer', 'Lighting', 'Other'].map(cat => (
                      <SelectItem key={cat} value={cat} className="text-gray-300">{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Description *</Label>
              <Textarea className="bg-gray-700 border-gray-600 text-white" rows={2}
                        value={equipmentForm.description} onChange={(e) => setEquipmentForm({...equipmentForm, description: e.target.value})} required />
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <Input type="number" className="bg-gray-700 border-gray-600 text-white" placeholder="Price (ZAR)"
                     value={equipmentForm.price} onChange={(e) => setEquipmentForm({...equipmentForm, price: e.target.value})} required />
              <Select value={equipmentForm.price_unit} onValueChange={(v) => setEquipmentForm({...equipmentForm, price_unit: v})}>
                <SelectTrigger className="bg-gray-700 border-gray-600 text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-700">
                  <SelectItem value="per day" className="text-gray-300">Per Day</SelectItem>
                  <SelectItem value="per hour" className="text-gray-300">Per Hour</SelectItem>
                </SelectContent>
              </Select>
              <Input type="number" min="1" className="bg-gray-700 border-gray-600 text-white" placeholder="Quantity"
                     value={equipmentForm.quantity_available} onChange={(e) => setEquipmentForm({...equipmentForm, quantity_available: parseInt(e.target.value) || 1})} />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Image *</Label>
              <div className="border-2 border-dashed border-gray-600 rounded-lg p-4 text-center cursor-pointer hover:border-yellow-500"
                   onClick={() => equipmentFileInputRef.current?.click()}>
                {equipmentImagePreview ? (
                  <img src={equipmentImagePreview} alt="Preview" className="max-h-32 mx-auto rounded" />
                ) : (
                  <div><Upload className="h-8 w-8 text-gray-500 mx-auto mb-2" /><p className="text-sm text-gray-500">Click to upload</p></div>
                )}
                <input ref={equipmentFileInputRef} type="file" accept="image/*" className="hidden" onChange={handleEquipmentImageChange} />
              </div>
            </div>
            {formError && <div className="p-3 bg-red-900/50 text-red-400 rounded text-sm">{formError}</div>}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowEquipmentModal(false)}
                      className="border-gray-600 text-gray-300">Cancel</Button>
              <Button type="submit" disabled={submitting} className="bg-yellow-500 hover:bg-yellow-600 text-black">
                {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Add Equipment
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BusinessDashboard;
